import { useCallback, useMemo } from 'react';

import Fuse from 'fuse.js/min-basic';
import { uniqBy } from 'lodash-es';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import type { LocationItem } from '#src/app/pages/search/shared/types';
import { removeDiacritics } from '#src/app/shared/utils/string';

type Result = ModelHookResult<
	undefined,
	{
		searchForCountryName: (queryLocation: string, selectedLocation: string) => readonly LocationItem[];
	}
>;

export function useSearchForCountryName(): Result {
	const {
		data: { countries },
	} = useSearchPageData();

	const fuseCountriesSet = useMemo(() => new Fuse(countries.map(({ displayName }) => displayName)), [countries]);

	return {
		actions: {
			searchForCountryName: useCallback(
				(queryLocation: string, selectedLocation: string) => {
					const filteredCountries = countries.filter((country) =>
						removeDiacritics(country.displayName.toLowerCase()).includes(removeDiacritics(queryLocation.toLowerCase())),
					);

					const codeExactMatch =
						queryLocation.length === 2
							? countries.filter((country) => country.countryCode.toLowerCase() === queryLocation.toLowerCase())
							: [];

					const fuzzyCountryMatch = fuseCountriesSet.search(queryLocation);
					const filteredFuzzyCountries = fuzzyCountryMatch
						.map(({ item: suggestedCountry }) => countries.find(({ displayName }) => displayName === suggestedCountry))
						.filter((country) => !!country);

					const uniqueResults = uniqBy(
						[...codeExactMatch, ...filteredCountries, ...filteredFuzzyCountries],
						'countryCode',
					);
					return uniqueResults.slice(0, 3).map((country) => ({
						value: country.countryCode,
						label: country.displayName,
						count: 0,
						isRefined: selectedLocation === country.countryCode,
						type: 'country' as const,
					}));
				},
				[countries, fuseCountriesSet],
			),
		},
	};
}
